<template>
  <div
    v-if="isVisible"
    class="w-full bg-lime-50 text-lime-700 text-sm px-4 py-2 border-l-4 border-lime-500 flex flex-row items-center justify-between fade-in"
  >
    <span>
      🎉 Happy birthday, {{ firstName }}! Thank you for being part of the Outtalent community!
    </span>
    <button
      class="ml-auto inline-flex items-center justify-center"
      type="button"
      aria-label="Dismiss birthday message"
      @click="dismissForADay"
    >
      <X class="w-4 h-4 opacity-50 hover:opacity-100 transition-opacity duration-200" />
    </button>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { X } from 'lucide-vue-next'
import { useStatsStore } from '@/stores/stats'
import { useUserStore } from '@/stores/user'

const statsStore = useStatsStore()
const userStore = useUserStore()

const firstName = computed(() => {
  return statsStore.stats?.current_user?.first_name || userStore.currentUser?.first_name || 'there'
})

const userId = computed<number | null>(() => {
  return userStore.currentUser?.id ?? statsStore.stats?.current_user?.id ?? null
})

const isBirthday = computed<boolean>(() => {
  return Boolean(statsStore.stats?.current_user?.is_birthday)
})

function getStorageKey(id: number) {
  return `birthday_banner_dismissed_${id}`
}

const isDismissedForToday = computed<boolean>(() => {
  if (!userId.value) return false
  try {
    const key = getStorageKey(userId.value)
    const raw = localStorage.getItem(key)
    if (!raw) return false
    const dismissedAt = Number(raw)
    if (Number.isNaN(dismissedAt)) return false
    const msSinceDismissed = Date.now() - dismissedAt
    return msSinceDismissed < 24 * 60 * 60 * 1000
  } catch {
    return false
  }
})

const dismissedNow = ref(false)

const isVisible = computed<boolean>(() => {
  return isBirthday.value && !isDismissedForToday.value && !dismissedNow.value
})

function dismissForADay() {
  if (!userId.value) return
  try {
    const key = getStorageKey(userId.value)
    localStorage.setItem(key, String(Date.now()))
  } catch {
    // ignore storage errors
  } finally {
    dismissedNow.value = true
  }
}
</script> 